import { useTranslation } from '@thoughtspot/i18n';
import { ContentGrid } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator/loading-indicator';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { hasModernFMPrivilege } from '../../../utils/feature-management-privilege';
import { CSV_UPLOAD_CONNECTORS, featureId as CSV_FEATURE_ID, ModalActions, } from '../../csv-upload/constants';
import { DisclaimerModal } from '../../csv-upload/disclaimer-modal/disclaimer-modal';
import { ConfigCard, InputType, SectionContainer, } from '../config-components';
import { ApplicationSettingsGroup, ApplicationSettingsTranslationKeys as Keys, ConfigPrePostFixModernAdminStrings, } from '../constants';
const CSV_UPLOAD_GROUP = ApplicationSettingsGroup.CSV_UPLOAD;
const tOrUndefined = (translated, key) => translated !== key ? translated : undefined;
const buildInputValidation = (configName, validation, t) => {
    if (!validation)
        return undefined;
    const [rangeMin, rangeMax] = validation.range
        ? validation.range.split('-').map(Number)
        : [undefined, undefined];
    const tWithFallback = (configKey, fallbackKey, params) => {
        const perConfigKey = `${configName}.${configKey}`;
        const perConfigVal = t(perConfigKey);
        return perConfigVal !== perConfigKey
            ? perConfigVal
            : t(fallbackKey, params);
    };
    return {
        required: validation.required === 'true',
        requiredError: tWithFallback(ConfigPrePostFixModernAdminStrings.REQUIREDERROR, Keys.VALIDATION_REQUIRED),
        regex: validation.regex,
        regexError: tWithFallback(ConfigPrePostFixModernAdminStrings.REGEXERROR, Keys.VALIDATION_INVALID_FORMAT),
        range: rangeMin !== undefined && rangeMax !== undefined
            ? { min: rangeMin, max: rangeMax }
            : undefined,
        rangeError: tWithFallback(ConfigPrePostFixModernAdminStrings.RANGEERROR, Keys.VALIDATION_OUT_OF_RANGE, { min: rangeMin, max: rangeMax }),
    };
};
const ConfigRow = ({ config, groupName, viewMode, onSave, onSaveOrgAware, onPreserveOverrideNeeded, isModernFMEnabled, }) => {
    const { t } = useTranslation();
    const [localValue, setLocalValue] = useState(config?.value ?? '');
    const isInputValid = useRef(true);
    useEffect(() => {
        setLocalValue(config?.value ?? '');
    }, [config?.value]);
    const doSave = useCallback((value, previousValue) => {
        if (!config?.configName)
            return;
        const rawOptions = [
            { optionKey: config.configName, optionValue: value },
        ];
        if (config.orgAware &&
            isModernFMEnabled &&
            hasModernFMPrivilege()) {
            if (viewMode === 'all') {
                onPreserveOverrideNeeded(config.configName, value, t(`${config.configName}.${ConfigPrePostFixModernAdminStrings.CONFIGLABEL}`), previousValue, () => setLocalValue(previousValue));
            }
            else {
                onSaveOrgAware(config.configName, value, previousValue, () => setLocalValue(previousValue));
            }
        }
        else {
            onSave(groupName, rawOptions, viewMode);
        }
    }, [
        config,
        groupName,
        viewMode,
        onSave,
        onSaveOrgAware,
        onPreserveOverrideNeeded,
        isModernFMEnabled,
    ]);
    const debouncedSave = useCallback(_.debounce((value, previousValue) => doSave(value, previousValue), 1000), [doSave]);
    const inputValidation = buildInputValidation(config?.configName ?? '', config?.validation, t);
    if (!config?.configName || !config.elementType)
        return null;
    const elementType = config.elementType.toLowerCase();
    const configLabel = t(`${config.configName}.${ConfigPrePostFixModernAdminStrings.CONFIGLABEL}`);
    const configDescKey = `${config.configName}.${ConfigPrePostFixModernAdminStrings.CONFIGDESC}`;
    const configDescription = tOrUndefined(t(configDescKey), configDescKey);
    if (elementType === InputType.Toggle) {
        return (React.createElement(ConfigCard, { inputType: InputType.Toggle, title: configLabel, description: configDescription, checked: localValue === 'true', name: config.configName, onChange: () => {
                const prev = localValue;
                const next = prev === 'true' ? 'false' : 'true';
                setLocalValue(next);
                doSave(next, prev);
            } }));
    }
    if (elementType === InputType.Select) {
        const rawOptions = config.elementConfig?.groups?.[0]?.options ?? [];
        const options = rawOptions.map(o => ({
            id: String(o),
            label: t(`${config.configName}.${o}.${ConfigPrePostFixModernAdminStrings.CONFIGLABEL}`),
        }));
        return (React.createElement(ConfigCard, { inputType: InputType.Select, "data-testid": config.configName, title: configLabel, value: localValue, options: options, onChange: (val) => {
                const prev = localValue;
                setLocalValue(val);
                doSave(val, prev);
            } }));
    }
    if (elementType === InputType.Input) {
        const subType = config.elementConfig?.type ?? 'text';
        const rangeMin = inputValidation?.range?.min;
        const rangeMax = inputValidation?.range?.max;
        return (React.createElement(ConfigCard, { inputType: InputType.Input, type: subType, "data-testid": config.configName, title: configLabel, description: configDescription, value: localValue, maxLength: config.validation?.maxLength, min: rangeMin, max: rangeMax, validation: inputValidation, onValidationError: (hasError) => {
                isInputValid.current = !hasError;
            }, onChange: (val) => {
                const prev = localValue;
                setLocalValue(val);
                if (isInputValid.current || val === '')
                    debouncedSave(val, prev);
            } }));
    }
    return null;
};
const CsvUploadTab = ({ group, viewMode, onSave }) => {
    const { t } = useTranslation();
    const rawValue = group.settings
        ?.find(s => s?.settingName === 'csvUpload')
        ?.configs?.find(c => c?.configName === CSV_FEATURE_ID)?.value ?? '';
    const enabledIds = rawValue ? rawValue.split(',').filter(Boolean) : [];
    const [connectors, setConnectors] = useState(CSV_UPLOAD_CONNECTORS.map(c => ({
        ...c,
        enabled: enabledIds.includes(c.id),
    })));
    const [disclaimerOpen, setDisclaimerOpen] = useState(false);
    const [pendingConnectors, setPendingConnectors] = useState([]);
    const commitSave = useCallback((updated) => {
        const activeIds = updated
            .filter(c => c.enabled)
            .map(c => c.id);
        onSave(CSV_UPLOAD_GROUP, [
            {
                optionKey: CSV_FEATURE_ID,
                optionValue: activeIds.join(','),
            },
        ], viewMode);
    }, [onSave, viewMode]);
    const handleToggle = useCallback((connectorId, enabling) => {
        if (enabling) {
            const next = connectors.map(c => c.id === connectorId ? { ...c, enabled: true } : c);
            setPendingConnectors(next);
            setDisclaimerOpen(true);
        }
        else {
            const next = connectors.map(c => c.id === connectorId ? { ...c, enabled: false } : c);
            setConnectors(next);
            commitSave(next);
        }
    }, [connectors, commitSave]);
    const handleDisclaimerAction = useCallback((action) => {
        setDisclaimerOpen(false);
        if (action === ModalActions.CONFIRM) {
            setConnectors(pendingConnectors);
            commitSave(pendingConnectors);
        }
    }, [pendingConnectors, commitSave]);
    return (React.createElement(React.Fragment, null,
        React.createElement(SectionContainer, { title: t(Keys.TAB_CSV_UPLOAD), description: t(Keys.TAB_CSV_UPLOAD_SECTION_TITLE) }, connectors.map(connector => (React.createElement(ConfigCard, { key: connector.id, inputType: InputType.Toggle, "data-testid": connector.id, title: connector.name, checked: connector.enabled, name: connector.id, onChange: () => handleToggle(connector.id, !connector.enabled) })))),
        React.createElement(DisclaimerModal, { isModalOpen: disclaimerOpen, setIsModalOpen: handleDisclaimerAction })));
};
export const ApplicationSettingsTab = ({ groups, isLoading, onSave, onReset, isSaving, viewMode, onSaveOrgAware, onPreserveOverrideNeeded, isModernFMEnabled, }) => {
    const { t } = useTranslation();
    const content = (React.createElement(React.Fragment, null, groups.map(group => {
        if (!group)
            return null;
        if (group.groupName === CSV_UPLOAD_GROUP) {
            return (React.createElement(CsvUploadTab, { key: group.groupName, group: group, viewMode: viewMode, onSave: onSave }));
        }
        return (group.settings ?? []).map(setting => {
            if (!setting)
                return null;
            const configs = (setting.configs ?? []).filter(Boolean);
            if (configs.length === 0)
                return null;
            const sectionTitle = t(`${setting.settingName}.${ConfigPrePostFixModernAdminStrings.SECTIONTITLE}`);
            const sectionSubTitleKey = `${setting.settingName}.${ConfigPrePostFixModernAdminStrings.SECTIONSUBTITLE}`;
            const sectionSubTitle = tOrUndefined(t(sectionSubTitleKey), sectionSubTitleKey);
            return (React.createElement(SectionContainer, { key: `${group.groupName}-${setting.settingName}`, "data-testid": setting.settingName, title: sectionTitle, description: sectionSubTitle, isCollapsible: configs.length > 1, onReset: setting.resetToDefault === 'true' &&
                    viewMode === 'all'
                    ? () => onReset(group.groupName, configs.map(c => c.configName))
                    : undefined }, configs.map(config => (React.createElement(ConfigRow, { key: config.configName, config: config, groupName: group.groupName, viewMode: viewMode, onSave: onSave, onSaveOrgAware: onSaveOrgAware, onPreserveOverrideNeeded: onPreserveOverrideNeeded, isModernFMEnabled: isModernFMEnabled })))));
        });
    })));
    return (React.createElement(LoadingIndicator.Global, { hideLoader: !isLoading && !isSaving },
        React.createElement(ContentGrid, { as: "section", span: 10, grow: true }, content)));
};
//# sourceMappingURL=application-settings-tab.js.map